// File: C:\Users\s-naidu\Desktop\merchant-card\frontend\src\merchant\pages\forms\Form2Representative.tsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MerchantLayout from "../../components/MerchantLayout";
import { apiGet, apiPost } from "../../api";

type Form2 = {
  rep_name: string;
  rep_name_kana: string;
  rep_email: string;
  birth_date: string;
  gender: string;
  spouse: string;

  // UI field names (keep these)
  home_postal: string;
  home_city: string;
  home_address: string;

  housing_type: string;
  years_residence: string;
  rep_phone: string;
  rep_mobile: string;
  career: string;

  is_completed: boolean;
};





type CareerFlag = "入学" | "卒業" | "入社" | "退社" | "開業" | "設立";

type CareerRow = {
  year: string;
  month: string;
  detail: string;
  flags: CareerFlag[];
};

const CAREER_FLAG_OPTIONS: CareerFlag[] = ["入学", "卒業", "入社", "退社", "開業", "設立"];

const createEmptyCareerRow = (): CareerRow => ({
  year: "",
  month: "",
  detail: "",
  flags: [],
});

const createDefaultCareerRows = (count = 10): CareerRow[] =>
  Array.from({ length: count }, () => createEmptyCareerRow());

const normalizeCareerRows = (rows: CareerRow[], count = 10): CareerRow[] => {
  const safeRows = rows.slice(0, count).map((row) => ({
    year: row.year || "",
    month: row.month || "",
    detail: row.detail || "",
    flags: Array.isArray(row.flags) ? row.flags.filter((f) => CAREER_FLAG_OPTIONS.includes(f)) : [],
  }));

  while (safeRows.length < count) {
    safeRows.push(createEmptyCareerRow());
  }

  return safeRows;
};

const parseCareerText = (text: string): CareerRow[] => {
  const raw = (text || "").trim();
  if (!raw) return createDefaultCareerRows();

  const lines = raw
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const parsed: CareerRow[] = lines.map((line) => {
    const parts = line.split("|").map((part) => part.trim());
    const year = parts[0] || "";
    const month = parts[1] || "";
    const detail = parts[2] || "";
    const flags = (parts[3] || "")
      .split(",")
      .map((flag) => flag.trim())
      .filter((flag): flag is CareerFlag => CAREER_FLAG_OPTIONS.includes(flag as CareerFlag));

    return { year, month, detail, flags };
  });

  return normalizeCareerRows(parsed);
};

const formatCareerRows = (rows: CareerRow[]): string => {
  return rows
    .map((row) => ({
      year: row.year.trim(),
      month: row.month.trim(),
      detail: row.detail.trim(),
      flags: row.flags,
    }))
    .filter((row) => row.year || row.month || row.detail || row.flags.length > 0)
    .map((row) => [row.year, row.month, row.detail, row.flags.join(",")].join(" | "))
    .join("\n");
};





const API_BASE = "/api";

const empty: Form2 = {
  rep_name: "",
  rep_name_kana: "",
  rep_email: "",
  birth_date: "",
  gender: "",
  spouse: "",
  home_postal: "",
  home_city: "",
  home_address: "",
  housing_type: "",
  years_residence: "",
  rep_phone: "",
  rep_mobile: "",
  career: "",
  is_completed: false,
};

export default function Form2Representative() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [data, setData] = useState<Form2>(empty);


  const [careerRows, setCareerRows] = useState<CareerRow[]>(createDefaultCareerRows());

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const formatBirthDate = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);

  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}/${digits.slice(4)}`;
  return `${digits.slice(0, 4)}/${digits.slice(4, 6)}/${digits.slice(6)}`;
  };



  const toDateInputValue = (value: string) => {
  return value ? value.replace(/\//g, "-") : "";
};

const fromDateInputValue = (value: string) => {
  return value ? value.replace(/-/g, "/") : "";
};


    const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);

    if (digits.length <= 3) return digits;

    if (digits.length <= 10) {
      if (digits.startsWith("03") || digits.startsWith("06")) {
        if (digits.length <= 6) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
        return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
      }

      if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  };

  // ✅ Step③: 「（必須）」が付いている項目だけで判定する
  const careerText = useMemo(() => formatCareerRows(careerRows), [careerRows]);

const canComplete = useMemo(() => {
  return (
    data.rep_name.trim() !== "" &&
    data.rep_name_kana.trim() !== "" &&
    data.birth_date.trim() !== "" &&
    data.gender.trim() !== "" &&
    data.spouse.trim() !== "" &&
    data.home_postal.trim() !== "" &&
    data.home_city.trim() !== "" &&
    data.home_address.trim() !== "" &&
    data.rep_mobile.trim() !== "" &&
    careerText.trim() !== ""
  );
}, [data, careerText]);

  const statusLabel = canComplete ? "入力済み" : "未完了";
  

  useEffect(() => {
    const run = async () => {
      setError("");
      setSuccess("");

      try {
        // ✅ GET is correct (router prefix is /merchant/forms)
        const json = await apiGet("/merchant/forms/representative");
        const d = json?.data;

        if (d) {
          // ✅ map backend keys -> UI keys
          setData({
            rep_name: d.rep_name || "",
            rep_name_kana: d.rep_name_kana || "",
            rep_email: d.rep_email || "",
            birth_date: d.birth_date || "",
            gender: d.gender || "",
            spouse: d.spouse || "",

            home_postal: d.home_postal_code || "",
            home_city: d.home_city || "",
            home_address: d.home_address_rest || "",

            housing_type: d.housing_type || "",
            years_residence: d.residence_years != null ? String(d.residence_years) : "",
            rep_phone: d.rep_phone_landline || "",
            rep_mobile: d.rep_phone_mobile || "",
            career: d.career || "",

            is_completed: Boolean(d.is_completed),
          });
          setCareerRows(parseCareerText(d.career || ""));
        } else {
          setData(empty);
          setCareerRows(createDefaultCareerRows());
        }

        setLoading(false);
      } catch (e: any) {
        setError(
          e?.message ||
            "代表者様情報の取得に失敗しました。バックエンドが起動しているか確認してください。"
        );
        setLoading(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    const onSave = async (): Promise<boolean> => {
    setError("");
    setSuccess("");
    setSaving(true);

    try {
      const json = await apiPost("/merchant/forms/representative", {
        rep_name: data.rep_name,
        rep_name_kana: data.rep_name_kana,
        rep_email: data.rep_email,
        birth_date: data.birth_date,
        gender: data.gender,
        spouse: data.spouse,

        home_postal_code: data.home_postal,
        home_city: data.home_city,
        home_address_rest: data.home_address,

        housing_type: data.housing_type,
        residence_years: data.years_residence ? Number(data.years_residence) : null,

        rep_phone_landline: data.rep_phone,
        rep_phone_mobile: data.rep_mobile,

        career: careerText,

        is_completed: canComplete,
      });

      setData((prev) => ({ ...prev, is_completed: canComplete }));
      const okCompleted = Boolean(json?.is_completed ?? canComplete);
      setSuccess(okCompleted ? "保存しました（入力済み）" : "保存しました（未完了）");

      setSaving(false);
      return true;
    } catch (e: any) {
      setError(e?.message || "保存に失敗しました。バックエンドが起動しているか確認してください。");
      setSaving(false);
      return false;
    }
  };




    const onNext = async () => {
    const ok = await onSave();
    if (!ok) return;
    navigate("/merchant/forms/payment-handling");
  };


    const onBack = async () => {
    const ok = await onSave();
    if (!ok) return;
    navigate("/merchant/forms/company-overview");
  };


  const onList = async () => {
  const ok = await onSave();
  if (!ok) return;
  navigate("/merchant/register");
};





  return (
    <MerchantLayout title="2. 代表者様情報" active="forms">
      {loading && <div style={{ color: "#475569" }}>読み込み中...</div>}

      {error && (
        <div
          style={{
            marginBottom: 12,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#b91c1c",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 800,
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          style={{
            marginBottom: 12,
            background: "#ecfdf5",
            border: "1px solid #bbf7d0",
            color: "#166534",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 900,
          }}
        >
          {success}（いつでも編集できます）
        </div>
      )}

      {!loading && (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
              gap: 20,
              alignItems: "start",
            }}
          >
            <Field label="代表者名（全角）" required>
  <input
    style={inputStyle}
    value={data.rep_name}
    onChange={(e) => setData({ ...data, rep_name: e.target.value })}
    placeholder="例）山田 太郎（全角）"
  />
</Field>

<Field label="代表者名フリガナ（半角）" required>
  <input
    style={inputStyle}
    value={data.rep_name_kana}
    onChange={(e) => setData({ ...data, rep_name_kana: e.target.value })}
    placeholder="例）ﾔﾏﾀﾞ ﾀﾛｳ"
  />
</Field>



<Field label="メールアドレス">
  <input
    type="email"
    style={inputStyle}
    value={data.rep_email}
    onChange={(e) => setData({ ...data, rep_email: e.target.value })}
    placeholder="例）example@company.co.jp"
  />
</Field>

  <Field label="生年月日" required>
  <input
    type="date"
    style={inputStyle}
    value={toDateInputValue(data.birth_date)}
    onChange={(e) =>
      setData({ ...data, birth_date: fromDateInputValue(e.target.value) })
    }
  />
</Field>

            <Field label="性別" required>
              <select
                style={inputStyle}
                value={data.gender}
                onChange={(e) => setData({ ...data, gender: e.target.value })}
              >
                <option value="">選択してください</option>
                {["男", "女", "その他"].map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="配偶者有無" required>
              <select
                style={inputStyle}
                value={data.spouse}
                onChange={(e) => setData({ ...data, spouse: e.target.value })}
              >
                <option value="">選択してください</option>
                {["有", "無"].map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </Field>

                        <Field label="自宅住所 郵便番号" required>
              <input
                style={inputStyle}
                value={data.home_postal}
                onChange={async (e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 7);
                  setData((prev) => ({ ...prev, home_postal: value }));

                  if (value.length === 7) {
                    try {
                      const res = await fetch(`${API_BASE}/postal/${value}`);
                      const json = await res.json();

                      if (json?.ok && json?.data) {
                        setData((prev) => ({
                          ...prev,
                          home_postal: value,
                          home_city: json.data.address_city || "",
                          home_address: json.data.address_rest || "",
                        }));
                      }
                    } catch (err) {
                      console.error("Postal lookup failed", err);
                    }
                  }
                }}
                placeholder="例）1234567"
              />
            </Field>

            <Field label="自宅住所（都道府県・市区町村・全角）" required>
              <input
                style={inputStyle}
                value={data.home_city}
                onChange={(e) => setData({ ...data, home_city: e.target.value })}
                placeholder="例）東京都渋谷区（全角）"
              />
            </Field>

            <Field label="自宅住所（番地以降・全角）" required>
              <input
                style={inputStyle}
                value={data.home_address}
                onChange={(e) => setData({ ...data, home_address: e.target.value })}
                placeholder="例）〇〇１－２－３ 〇〇ビル１０Ｆ（全角）"
              />
            </Field>

            <Field label="住居形態">
              <select
                style={inputStyle}
                value={data.housing_type}
                onChange={(e) => setData({ ...data, housing_type: e.target.value })}
              >
                <option value="">選択してください</option>
                {["自己所有", "賃貸", "家族所有", "その他"].map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </Field>

           <Field label="居住年数">
  <select
    style={inputStyle}
    value={data.years_residence}
    onChange={(e) => setData({ ...data, years_residence: e.target.value })}
  >
    <option value="">選択してください</option>
    {Array.from({ length: 100 }, (_, i) => {
      const year = String(i + 1);
      return (
        <option key={year} value={year}>
          {year}年
        </option>
      );
    })}
  </select>
</Field>

                                    <Field label="代表者固定電話">
              <input
                style={inputStyle}
                value={data.rep_phone}
                onChange={(e) =>
                  setData({ ...data, rep_phone: formatPhoneNumber(e.target.value) })
                }
                placeholder="例）03-1234-5678"
              />
            </Field>

                                    <Field label="代表者携帯" required>
              <input
                style={inputStyle}
                value={data.rep_mobile}
                onChange={(e) =>
                  setData({ ...data, rep_mobile: formatPhoneNumber(e.target.value) })
                }
                placeholder="例）090-1234-5678"
              />
            </Field>

                        <Field label="経歴" required full>
              <div
                style={{
                  border: "1px solid #cbd5e1",
                  borderRadius: 12,
                  overflow: "hidden",
                  background: "#fff",
                }}
              >
                <div
                  style={{
                    padding: "10px 12px",
                    background: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                    fontSize: 13,
                    color: "#475569",
                    fontWeight: 800,
                  }}
                >
                  上から順にご入力ください。最大10行まで入力できます。
                </div>

                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      minWidth: 980,
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={{ ...careerHeaderCellStyle, width: 90 }}>年</th>
                        <th style={{ ...careerHeaderCellStyle, width: 90 }}>月</th>
                        <th style={careerHeaderCellStyle}>内容</th>
                        <th style={{ ...careerHeaderCellStyle, width: 280 }}>区分</th>
                      </tr>
                    </thead>
                    <tbody>
                      {careerRows.map((row, index) => (
                        <tr key={index}>
                          <td style={careerBodyCellStyle}>
                            <input
                              type="text"
                              inputMode="numeric"
                              maxLength={4}
                              value={row.year}
                              onChange={(e) => {
                                const year = e.target.value.replace(/\D/g, "").slice(0, 4);
                                setCareerRows((prev) =>
                                  updateCareerRow(prev, index, (current) => ({
                                    ...current,
                                    year,
                                  }))
                                );
                              }}
                              placeholder={index === 0 ? "例) 2015" : ""}
                              style={careerCellInputStyle}
                            />
                          </td>

                          <td style={careerBodyCellStyle}>
                            <input
                              type="text"
                              inputMode="numeric"
                              maxLength={2}
                              value={row.month}
                              onChange={(e) => {
                                const month = e.target.value.replace(/\D/g, "").slice(0, 2);
                                setCareerRows((prev) =>
                                  updateCareerRow(prev, index, (current) => ({
                                    ...current,
                                    month,
                                  }))
                                );
                              }}
                              placeholder={index === 0 ? "例) 04" : ""}
                              style={careerCellInputStyle}
                            />
                          </td>

                          <td style={careerBodyCellStyle}>
                            <input
                              type="text"
                              value={row.detail}
                              onChange={(e) => {
                                setCareerRows((prev) =>
                                  updateCareerRow(prev, index, (current) => ({
                                    ...current,
                                    detail: e.target.value,
                                  }))
                                );
                              }}
                              placeholder={
                                index === 0 ? "例) ○○大学 / 株式会社○○ / 自営業開始" : ""
                              }
                              style={careerCellInputStyle}
                            />
                          </td>

                          <td style={careerBodyCellStyle}>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(3, minmax(72px, 1fr))",
                                gap: 8,
                              }}
                            >
                              {CAREER_FLAG_OPTIONS.map((flag) => {
                                const checked = row.flags.includes(flag);

                                return (
                                  <label
                                    key={flag}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                      fontSize: 13,
                                      color: "#0f172a",
                                      fontWeight: 700,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) => {
                                        setCareerRows((prev) =>
                                          updateCareerRow(prev, index, (current) => ({
                                            ...current,
                                            flags: e.target.checked
                                              ? [...current.flags, flag]
                                              : current.flags.filter((item) => item !== flag),
                                          }))
                                        );
                                      }}
                                    />
                                    {flag}
                                  </label>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Field>
          </div>

                              <div
            style={{
              marginTop: 18,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <button
              onClick={onBack}
              disabled={saving}
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: "none",
                background: "#dc2626",
                color: "#fff",
                fontWeight: 900,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.7 : 1,
                transition: "background 0.2s ease",
              }}
            >
              {saving ? "保存中..." : "戻る"}
            </button>

            <button
              onClick={onSave}
              disabled={saving}
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: "none",
                background: "#1d4ed8",
                color: "#fff",
                fontWeight: 900,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.7 : 1,
                transition: "background 0.2s ease",
              }}
            >
              {saving ? "保存中..." : "保存"}
            </button>



            <button
  onClick={onList}
  disabled={saving}
  style={{
    padding: "12px 16px",
    borderRadius: 12,
    border: "none",
    background: "#64748b",
    color: "#fff",
    fontWeight: 900,
    cursor: saving ? "not-allowed" : "pointer",
    opacity: saving ? 0.7 : 1,
    transition: "background 0.2s ease",
  }}
>
  一覧
</button>

            <button
              onClick={onNext}
              disabled={saving}
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: "none",
                background: "#dc2626",
                color: "#fff",
                fontWeight: 900,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.7 : 1,
                transition: "background 0.2s ease",
              }}
            >
              {saving ? "保存中..." : "次へ"}
            </button>
          </div>

          <div style={{ marginTop: 10, fontSize: 13, color: "#475569" }}>
            ステータス：{" "}
            <span
              style={{
                fontWeight: 900,
                color: canComplete ? "#dc2626" : "#0b1f3b",
              }}
            >
              {statusLabel}
            </span>
          </div>

          {!canComplete && (
            <div style={{ fontSize: 12, color: "#475569", fontWeight: 800 }}>
              ※ 必須項目が未入力のため「未完了」になります
            </div>
          )}
        </>
      )}
    </MerchantLayout>
  );
}

/** ✅ Field label style */
function Field({
  label,
  required,
  full,
  children,
}: {
  label: string;
  required?: boolean;
  full?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{ minWidth: 0, gridColumn: full ? "1 / -1" : undefined }}>
      <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 6, color: "#0f172a" }}>
        {label} {required ? <span style={{ color: "#b91c1c" }}>（必須）</span> : null}
      </div>
      {children}
    </div>
  );
}

/** ✅ input styles */
const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 44,
  padding: "0 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};

const textareaStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 44,
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  resize: "vertical",
};


const careerCellInputStyle: React.CSSProperties = {
  width: "100%",
  height: 40,
  padding: "0 10px",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};

const careerHeaderCellStyle: React.CSSProperties = {
  padding: "10px 8px",
  background: "#eff6ff",
  borderBottom: "1px solid #cbd5e1",
  fontWeight: 900,
  fontSize: 13,
  color: "#0f172a",
  textAlign: "center",
  whiteSpace: "nowrap",
};

const careerBodyCellStyle: React.CSSProperties = {
  padding: 8,
  borderBottom: "1px solid #e2e8f0",
  verticalAlign: "top",
};

function updateCareerRow(
  rows: CareerRow[],
  index: number,
  updater: (row: CareerRow) => CareerRow
): CareerRow[] {
  return rows.map((row, i) => (i === index ? updater(row) : row));
}
